import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  getLoginBrand,
  type LoginBrand
} from '../../../infrastructure/auth/loginBrandApi';
import { applyLoginBrandColors } from '../../theme/presentation/applyWorkspaceTheme';

export type LoginBrandState = {
  /** True once a branded workspace has been resolved for the `?ws=` param. */
  branded: boolean;
  /** Workspace name to show where "OCTOPUS" shows; null when unbranded. */
  name: string | null;
  /** Logo URL to render in place of the OctopusLogo; null/'' when unbranded. */
  logoUrl: string | null;
  /**
   * True while a `?ws=` brand fetch is in-flight (before we know whether/what
   * to brand). The auth side-panel holds its logo/wordmark render while this is
   * true so a `?ws=` visitor never sees the default Octopus flash before the
   * workspace logo resolves. Always false when there's no `?ws=` param.
   */
  resolving: boolean;
};

const UNBRANDED: LoginBrandState = {
  branded: false,
  name: null,
  logoUrl: null,
  resolving: false
};

/**
 * Pre-auth brand resolver for the auth screens.
 *
 * When the URL carries `?ws=<uuid>` we fetch the public login-brand for that
 * workspace and, if it's opted into branding, apply its `--primary`/`--secondary`
 * colours (via the shared `applyWorkspaceTheme` injection) and hand back its
 * name + logo so the shell can swap the Octopus wordmark/logo. With no `?ws=`
 * — or a workspace that returns `branded: false` — nothing is applied and the
 * default Octopus login renders unchanged. Colours are reverted on unmount so
 * the branding never leaks past the auth screen.
 */
export const useLoginBrand = (): LoginBrandState => {
  const [searchParams] = useSearchParams();
  const workspaceId = searchParams.get('ws');
  // Seed ``resolving: true`` up-front when a ``?ws=`` is present so the very
  // first paint holds the branded slot instead of flashing the Octopus.
  const [state, setState] = useState<LoginBrandState>(() =>
    workspaceId ? { ...UNBRANDED, resolving: true } : UNBRANDED
  );

  useEffect(() => {
    if (!workspaceId) {
      applyLoginBrandColors(null);
      setState(UNBRANDED);
      return undefined;
    }

    let cancelled = false;
    // A new/changed ``?ws=`` re-enters the resolving state so the panel holds.
    setState({ ...UNBRANDED, resolving: true });

    getLoginBrand(workspaceId)
      .then((brand: LoginBrand) => {
        if (cancelled) return;
        if (!brand?.branded) {
          applyLoginBrandColors(null);
          setState(UNBRANDED);
          return;
        }
        applyLoginBrandColors(brand);
        setState({
          branded: true,
          name: brand.name || null,
          logoUrl: brand.logo_url || null,
          resolving: false
        });
      })
      .catch(() => {
        // Brand is non-critical — fall back to the Octopus default silently.
        if (cancelled) return;
        applyLoginBrandColors(null);
        setState(UNBRANDED);
      });

    return () => {
      cancelled = true;
      // Revert so the branding doesn't bleed into the post-login app.
      applyLoginBrandColors(null);
    };
  }, [workspaceId]);

  return state;
};
